import React from "react";
import { Button, Space, Tag, Typography } from "antd";
import { ApartmentOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import SectionCard from "../../../components/shared/SectionCard";
import InlineLoading from "../../../components/shared/InlineLoading";
import EmptyState from "../../../components/shared/EmptyState";
import ProgressDots from "../../healthcare/treatmentPlans/planComponents/planDrawerComponents/workflowProgress/ProgressDots";
import { DashboardActivePlan } from "../../../api/healthcare/DashboardApi";

const { Text } = Typography;

const stageTypeLabel: Record<string, string> = {
	trigger:    "Trigger",
	condition:  "Condition",
	action:     "Action",
	watcher:    "Waiting",
	appointment: "Appointment",
	manual:     "Review",
	sub_plan:   "Sub-plan",
};

interface TreatmentPlansPanelProps {
	activePlans: DashboardActivePlan[];
	stats: { total: number; active: number; paused: number; completed: number };
	isLoading: boolean;
	onOpenPlan?: (id: number) => void;
}

const TreatmentPlansPanel: React.FC<TreatmentPlansPanelProps> = ({ activePlans, stats, isLoading, onOpenPlan }) => {
	const navigate = useNavigate();

	return (
		<SectionCard
			title={
				<Space size={6} align="center">
					<ApartmentOutlined />
					<span>Treatment Plans</span>
				</Space>
			}
			extra={
				<Button
					size="small"
					icon={<PlusOutlined />}
					onClick={() => navigate("/healthcare/treatment-plans?action=create")}
				>
					New
				</Button>
			}
			style={{ height: "100%" }}
		>
			{isLoading ? (
				<InlineLoading minHeight={160} message="Loading plans…" />
			) : activePlans.length === 0 ? (
				<EmptyState
					title="No active treatment plans"
					action={{
						label: "Create Plan",
						icon: <PlusOutlined />,
						onClick: () => navigate("/healthcare/treatment-plans?action=create"),
					}}
					style={{ padding: "24px 0" }}
				/>
			) : (
				<div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
					{activePlans.map((plan, idx) => {
						const isLast = idx === activePlans.length - 1;
						const stageLabel = plan.current_stage
							? stageTypeLabel[plan.current_stage.stage_type] || plan.current_stage.stage_type
							: null;
						return (
							<div
								key={plan.id}
								onClick={() => onOpenPlan?.(plan.id)}
								style={{
									padding: "8px 0",
									borderBottom: isLast ? "none" : "1px solid var(--mg-border)",
									cursor: "pointer",
								}}
							>
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 2 }}>
									<Text style={{ fontSize: 12, fontWeight: 600 }} ellipsis>
										{plan.template?.name || `Plan #${plan.id}`}
									</Text>
									{stageLabel && (
										<Tag color="blue" style={{ fontSize: 10, flexShrink: 0 }}>
											{stageLabel}
										</Tag>
									)}
								</div>
								<Text style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4, marginBottom: 4, color: "var(--mg-text-secondary)" }}>
									<UserOutlined style={{ fontSize: 10 }} />
									{plan.patient_name || `Patient #${plan.patient_id}`}
								</Text>
								{plan.current_stage?.title && (
									<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
										{plan.current_stage.title}
									</Text>
								)}
								<ProgressDots stages={plan.stages ?? []} sections={plan.sections ?? []} />
							</div>
						);
					})}
				</div>
			)}
		</SectionCard>
	);
};

export default TreatmentPlansPanel;
